import React, { useEffect, useState, useMemo } from "react";
import { Box, Grid, Paper, Typography, TextField, Chip as MuiChip } from "@mui/material";
import { Row } from "simple-flexbox";
import { useSelector } from "react-redux";
import jwt_decode from "jwt-decode";

import { GameTable } from "./table.js";
import SelectTextFields from "../../components/customDropDown/dropDown";
import CustomizedButtons from "../../components/button/button";

import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { transcationArrayType, transcationMode, walletFilterOptions, ACCESS_LEVEL } from "../../constant/constant";
import { parseGameTranscation } from "../../helper";

const defaultDateTimeState = {
  date_from: "",
  date_to: "",
};

const defaultTransactionType = {
  value: "All",
  label: "All",
  _id: 0,
};

export default function Transaction() {
  const [dateTime, setDateTime] = useState(defaultDateTimeState);
  const [totalAmt, setTotalAmt] = useState(0);
  const [totalCashbackAmt, setTotalCashbackAmt] = useState(0);
  const [gameTranscation, setGameTranscation] = useState([]);
  const [transactionType, setTransactionType] = useState(defaultTransactionType);
  const bankModeDefault = { value: "BANK", label: "BANK", _id: 1 };
  const [transactionMode, setTransactionMode] = useState(defaultTransactionType);
  const [walletFilter, setWalletFilter] = useState(defaultTransactionType);
  const [transcation, setTranscation] = useState({});
  const [disableOtherFilters, setDisableOtherFilters] = useState(false);
  const [phoneOrPlayerId, setPhoneOrPlayerId] = useState("");
  const [offerApprovalFilter, setOfferApprovalFilter] = useState({ value: "All", _id: 0 });
  const [cashbackFilter, setCashbackFilter] = useState({ value: "All", _id: 0 });

  const offerApprovalOptions = [
    { value: "All", label: "All", _id: 0 },
    { value: "PENDING", label: "Pending Approval", _id: 1 },
    { value: "APPROVED", label: "Approved", _id: 2 },
    { value: "REJECTED", label: "Rejected", _id: 3 },
    { value: "AUTO", label: "Auto (No Approval)", _id: 4 },
  ];

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPage: 1,
    size: 20,
  });
  const [searched, setSearched] = useState(false);

  const [showOtherDeposits, setShowOtherDeposits] = useState(false);
  const [forceShowSummary, setForceShowSummary] = useState(false);

  // Gmail-style "g" then "i" key sequence reveals OTHER-type deposit transactions
  useEffect(() => {
    let lastKey = "";
    let lastKeyTime = 0;
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      const now = Date.now();
      if (e.key.toLowerCase() === "i" && lastKey === "g" && now - lastKeyTime < 1000) {
        setShowOtherDeposits((prev) => !prev);
        lastKey = "";
        return;
      }
      // "C" then "D" while Caps Lock is on -- the totals summary is hidden by default
      // (even after a normal search) and only this shortcut reveals it; it's a one-time
      // peek that hides again the next time a search actually runs (still hidden from
      // cashiers regardless).
      if (
        e.key.toLowerCase() === "d" &&
        lastKey === "c" &&
        now - lastKeyTime < 1000 &&
        e.getModifierState("CapsLock")
      ) {
        setForceShowSummary(true);
        lastKey = "";
        return;
      }
      lastKey = e.key.toLowerCase();
      lastKeyTime = now;
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const visibleTranscation = useMemo(
    () => showOtherDeposits
      ? gameTranscation
      : gameTranscation.filter((t) => !(t.txn_type === "DEPOSIT" && t.payment_mode === "OTHER")),
    [gameTranscation, showOtherDeposits]
  );

  const loginSlice = useSelector((state) => state.loginSlice);
  const token = loginSlice.token;
  const accessLevel = useMemo(() => {
    try { return token ? jwt_decode(token)?.accessLevel : 0; } catch { return 0; }
  }, [token]);
  const isBankOnly = accessLevel === 999;
  const isCashier = accessLevel === ACCESS_LEVEL.CASHIER;

  const getGameTranscation = async (page = 1, size = 20, refreshData) => {
    let postData = await parseGameTranscation(
      transcation,
      transactionType,
      dateTime,
      page,
      size,
      transactionMode,
      walletFilter,
    );
    if (offerApprovalFilter?.value && offerApprovalFilter.value !== "All") {
      postData.offer_approval_status = offerApprovalFilter.value;
    }
    if (cashbackFilter?.value === "HAS_CASHBACK") {
      postData.has_cashback = true;
    }

    if (refreshData) postData = refreshData;

    const url = `${apiBaseUrl}/transaction`;
        console.log("postData",postData);
    const res = await axiosRequest("POST", url, token, postData);

    if (res?.status === "success") {
      setGameTranscation(res.data.transactionList);
      setPagination(res.data.pagniationData);
      setTotalAmt(res.data.totalTnxAmt || 0);
      setTotalCashbackAmt(res.data.totalCashbackAmt || 0);
      setSearched(true);
      setForceShowSummary(false);
    }
  };

  useEffect(() => {
    if (isBankOnly) setTransactionMode(bankModeDefault);
    getGameTranscation();
  }, [isBankOnly]);

  const handleSubmit = () => {
    getGameTranscation();
  };

  const handleReset = () => {
    setDateTime(defaultDateTimeState);
    setTranscation({});
    setTransactionType(defaultTransactionType);
    setTransactionMode(isBankOnly ? bankModeDefault : defaultTransactionType);
    setWalletFilter(defaultTransactionType);
    setOfferApprovalFilter({ value: "All", _id: 0 });
    setCashbackFilter({ value: "All", _id: 0 });
    setDisableOtherFilters(false);
    setSearched(false);
    setTotalAmt(0);
    setTotalCashbackAmt(0);
    setPhoneOrPlayerId("");
    getGameTranscation(1, 20, defaultDateTimeState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setTranscation((prev) => {
      const updated = { ...prev };
      delete updated.phone;
      delete updated.player_id;

      if (name === "phone_or_player_id") {
        setPhoneOrPlayerId(value);
        const trimmed = value.trim();
        if (trimmed) {
          if (/^\d{10}$/.test(trimmed)) updated.phone = trimmed;
          else updated.player_id = trimmed;
        }
      } else {
        if (value.trim()) updated[name] = value.trim();
        else delete updated[name];
      }

      setDisableOtherFilters(Object.keys(updated).length > 0);

      return updated;
    });
  };

  const setDate = (date, key) => {
    setDateTime((prev) => ({ ...prev, [key]: date }));
  };

  const handleTransactionType = (label, value, _id = 1) => {
    setTransactionType({ value, _id });
  };

  const handleTransactionMode = (label, value, _id = 1) => {
    setTransactionMode({ value, _id });
  };

  return (
    <Box sx={{ width: "100%", marginTop: { xs: "55px", md: "95px" }, pb: 6 }}>

      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" gap={2} mb={3}>
          <Typography variant="h5">User Transaction</Typography>
          {isBankOnly && (
            <MuiChip label="Bank Mode Only" color="info" size="small" sx={{ fontWeight: 700 }} />
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Transaction Id"
              name="transaction_id"
              value={transcation?.transaction_id || ""}
              onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.transaction_id}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Phone / Player ID"
              name="phone_or_player_id"
              value={phoneOrPlayerId}
              onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.phone && !transcation?.player_id}
              helperText="10-digit number = phone; otherwise player ID"
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Player Name"
              name="name"
              value={transcation?.name || ""}
              onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.name}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="User Email"
              name="user_email"
              value={transcation?.user_email || ""}
              onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.user_email}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Transaction Type"
              value={transactionType?.value}
              handleOnChange={handleTransactionType}
              list={transcationArrayType}
            />
          </Grid>
          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Transaction Mode"
              value={transactionMode?.value}
              handleOnChange={isBankOnly ? undefined : handleTransactionMode}
              list={isBankOnly ? [bankModeDefault] : transcationMode}
              disabled={isBankOnly}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Offer Approval Status"
              value={offerApprovalFilter?.value}
              handleOnChange={(label, value, _id) => setOfferApprovalFilter({ value, _id })}
              list={offerApprovalOptions}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Deposit Offer Cashback"
              value={cashbackFilter?.value}
              handleOnChange={(label, value, _id) => setCashbackFilter({ value, _id })}
              list={[
                { value: "All",          label: "All",               _id: 0 },
                { value: "HAS_CASHBACK", label: "Has Cashback Only", _id: 1 },
              ]}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Wallet Deducted"
              value={walletFilter?.value}
              handleOnChange={(label, value, _id) => setWalletFilter({ value, _id })}
              list={walletFilterOptions}
            />
          </Grid>


          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Date & Time From"
              type="datetime-local"
              value={dateTime?.date_from || ""}
              onChange={(e) => setDate(e.target.value, "date_from")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Date & Time To"
              type="datetime-local"
              value={dateTime?.date_to || ""}
              onChange={(e) => setDate(e.target.value, "date_to")}
              InputLabelProps={{ shrink: true }}
              disabled={!dateTime?.date_from}
              inputProps={{ min: dateTime?.date_from || "" }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <CustomizedButtons
                color="success"
                label="SEARCH"
                variant="contained"
                onClick={handleSubmit}
              />
              <CustomizedButtons
                color="error"
                label="RESET"
                variant="contained"
                onClick={handleReset}
              />
            </Box>
          </Grid>

          {forceShowSummary && !isCashier && (
            <Grid item xs={12}>
              <Box display="flex" gap={3} alignItems="center" flexWrap="wrap"
                sx={{ p: 2, borderRadius: 2, background: "linear-gradient(135deg,#1e3c72,#2a5298)", color: "#fff" }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Transactions</Typography>
                  <Typography variant="h6" fontWeight={700}>{pagination.totalRecords ?? gameTranscation.length}</Typography>
                </Box>
                <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.3)", pl: 3 }}>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Amount</Typography>
                  <Typography variant="h5" fontWeight={800}>
                    {Number(totalAmt || 0).toLocaleString("en-IN")}
                  </Typography>
                </Box>
                {totalCashbackAmt > 0 && (
                  <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.3)", pl: 3 }}>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Cashback</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: "#ffd54f" }}>
                      {Number(totalCashbackAmt).toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {searched && visibleTranscation?.length === 0 ? (
        <Paper sx={{ mt: 3, p: 6, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">No transactions found</Typography>
          <Typography variant="body2" color="text.disabled" mt={1}>Try adjusting your filters and search again.</Typography>
        </Paper>
      ) : visibleTranscation?.length > 0 ? (
        <Paper sx={{ mt: 3 }}>
          <Row justifyContent="center" style={{ width: "100%" }}>
            <GameTable
              gameTranscation={visibleTranscation}
              pagination={pagination}
              getGameTranscation={getGameTranscation}
            />
          </Row>
        </Paper>
      ) : null}
    </Box>
  );
}
